import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSupplierRetailer } from "./SupplierRetailerContext";
import { usePageAccess } from "./PageAccessContext";
import { getDeviceId } from "../tools/deviceId";
import { SESSION_ENDED_EVENT } from "../tools/demoInterceptor";

const AuthContext = createContext();

export const useAuthentication = () => useContext(AuthContext);

// localStorage keys that describe *the machine*, not *the session*. Sign-out
// must not wipe these — clearing `staySignedIn` is what used to silently
// un-tick "Remember this device" every time someone signed out, so the next
// sign-in minted no refresh token and the reload bounced them back to /signin.
const DEVICE_PREF_KEYS = ["staySignedIn", "spearbi_nav_collapsed"];

// A session is restorable only if the user asked us to remember the device
// AND we hold both halves of the credential (token + who it belongs to).
const hasStoredSession = () =>
  localStorage.getItem("staySignedIn") === "true" &&
  !!localStorage.getItem("refreshToken") &&
  !!localStorage.getItem("userid");

const clearSessionKeys = () => {
  const keep = DEVICE_PREF_KEYS.reduce((acc, k) => {
    const v = localStorage.getItem(k);
    if (v !== null) acc[k] = v;
    return acc;
  }, {});
  localStorage.clear();
  Object.entries(keep).forEach(([k, v]) => localStorage.setItem(k, v));
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// One-line message the sign-in page shows after the app sent the user there
// itself (2026-10-15) — never a blank app without a word.
export const SIGNIN_NOTICE_KEY = "spearbi_signin_notice";
const setSigninNotice = (text) => {
  try {
    sessionStorage.setItem(SIGNIN_NOTICE_KEY, text);
  } catch (e) {
    // ignore — sessionStorage may be unavailable
  }
};

// ── Teleport (2026-09-17, docs/michael/CHANGES-2026-09-17-teleport.md) ────────
// A master "teleports" into another account: the master's session keys are
// parked in ONE localStorage entry and the live keys become the target's (a
// 2 h impersonation token minted by /admin/teleport-in — no refresh token). The
// badge in the shell renders only from the `teleport` state below, which only a
// successful teleport-in sets; a normal user never holds the stash and never
// reaches the page that offers it.
const TELEPORT_KEY = "spearbi_teleport";
// Everything that describes the signed-in identity (not the device).
const SESSION_KEYS = ["userid", "fname", "user_type", "username", "accessToken", "refreshToken", "user_org_code", "user_org_name", "spearbi_org", "usersView"];

const readTeleport = () => {
  try {
    const raw = localStorage.getItem(TELEPORT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const putSessionKeys = (values) => {
  SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
  Object.entries(values || {}).forEach(([k, v]) => {
    if (SESSION_KEYS.includes(k) && v !== null && v !== undefined) localStorage.setItem(k, v);
  });
};

// A teleport has no refresh token, so it cannot survive a page load. On a cold
// load with a stash present the master's own keys go back FIRST, and the normal
// restore (refresh token) then runs for the master — a reload ends the teleport.
const restoreMasterFromStashIfAny = () => {
  const t = readTeleport();
  if (!t) return false;
  putSessionKeys(t.stash || {});
  localStorage.removeItem(TELEPORT_KEY);
  return true;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user_type, setUserType] = useState(null);
  // True only while we are exchanging a stored refresh token on a cold load.
  // RequireAuth holds the route instead of redirecting for as long as this is
  // set, which is what actually keeps a reload from dumping you on /signin.
  const [restoring, setRestoring] = useState(() => {
    restoreMasterFromStashIfAny();
    return hasStoredSession();
  });
  // Who the master is teleported into, or null. Never persisted across loads.
  const [teleport, setTeleport] = useState(null);
  const navigate = useNavigate();
  const { fetchUserAccess, resetContext: resetSupplierRetailerContext } = useSupplierRetailer();
  const { fetchPageAccess, resetContext: resetPageAccessContext } = usePageAccess();

  const signIn = (user_type = null) => {
    // New Data popup dismiss is per session — a fresh sign-in must show it
    // again while items are pending (TASK-new-data-tab.md §6 decision 3).
    try {
      sessionStorage.removeItem("spearbi_newdata_dismissed");
    } catch (e) {
      // ignore — sessionStorage may be unavailable
    }
    setIsAuthenticated(true);
    if (user_type !== null) {
      setUserType(user_type);
      localStorage.setItem("user_type", user_type);
    }
  };

  // Cold-load session restore. Runs once, inside the provider, *before* the
  // router is allowed to decide anything — the old version lived in a hook on
  // AppRoutes and lost the race to RequireAuth's <Navigate> every single time.
  useEffect(() => {
    if (!restoring) return;
    let cancelled = false;

    const run = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const refreshToken = localStorage.getItem("refreshToken");

      // A cold laptop, a reconnecting VPN or a sleeping API dyno must not cost
      // the user their session, so a failed *call* is retried. Only a clean
      // "this token is no good" answer from the server ends the session.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const r = await axios.post(`${apiUrl}/login/refresh`, { refreshToken, deviceId: getDeviceId() });
          const row = r.status === 201 ? r.data?.data?.[0] : null;

          if (!row) {
            // 2xx but not 201 — the server rejected the token itself
            // (expired / revoked / unknown). Retrying cannot help.
            clearSessionKeys();
            setSigninNotice(
              r.data?.reason === "inactive"
                ? "Your account is switched off. Please contact helena@spearmanagement.co.za."
                : "Your session has ended. Please sign in again."
            );
            break;
          }

          localStorage.setItem("userid", row.userid);
          localStorage.setItem("fname", row.fname);
          localStorage.setItem("user_type", row.user_type || "default");
          if (row.username) localStorage.setItem("username", row.username);
          // Rotated / slid token, when the API sends one back.
          if (r.data.refreshToken) localStorage.setItem("refreshToken", r.data.refreshToken);
          // Fresh access token from the refresh exchange — store before the
          // (authenticated) access/page fetches below.
          if (r.data.accessToken) localStorage.setItem("accessToken", r.data.accessToken);

          const okUser = await fetchUserAccess(row.userid);
          const okPages = await fetchPageAccess(row.userid);
          if (cancelled) return;
          if (!okUser || !okPages) {
            // Token is fine but the account would not load (network / host
            // block). Do not open a blank app — go to sign-in and say so. The
            // refresh token is kept, so the next reload tries again.
            setSigninNotice("We couldn't load your account. Please check your connection and sign in again.");
            break;
          }

          setUserType(row.user_type || "default");
          setIsAuthenticated(true);
          setRestoring(false);
          return;
        } catch (err) {
          // Transport-level failure. Keep the token and try again shortly.
          if (attempt < 2) {
            await sleep(500 * 2 ** attempt);
            if (cancelled) return;
          } else {
            setSigninNotice("We couldn't reach Spear. Please check your connection and sign in again.");
          }
        }
      }

      if (cancelled) return;
      setRestoring(false);
    };

    run();
    return () => {
      cancelled = true;
    };
    // Once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The API refused to renew the session (switched off, revoked, expired) —
  // tools/demoInterceptor.js fires this. End it cleanly on the sign-in page.
  useEffect(() => {
    const onEnded = (e) => {
      if (!localStorage.getItem("userid")) return; // already signed out
      if (readTeleport()) {
        // A teleport token simply ran out — go back to the master's account.
        teleportOut();
        return;
      }
      setSigninNotice(
        e?.detail?.reason === "inactive"
          ? "Your account is switched off. Please contact helena@spearmanagement.co.za."
          : "Your session has ended. Please sign in again."
      );
      setIsAuthenticated(false);
      setUserType(null);
      setRestoring(false);
      resetSupplierRetailerContext();
      resetPageAccessContext();
      clearSessionKeys();
      navigate("/signin");
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Become another account in place — no page load (a teleport has no refresh
  // token, so a load would land on /signin). Resolves with the API's teleport
  // summary; throws on refusal so the caller can toast the reason.
  const teleportIn = async (target) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const r = await axios.post(`${apiUrl}/admin/teleport-in`, { userid: target.userid });
    const row = r.data?.data?.[0];
    const accessToken = r.data?.accessToken;
    if (!row || !accessToken) throw new Error(r.data?.message || "Teleport failed");

    const stash = {};
    SESSION_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) stash[k] = v;
    });
    const meta = {
      stash,
      returnTo: `${window.location.pathname}${window.location.search}`,
      from: r.data.teleport?.from || { userid: stash.userid, fname: stash.fname },
      target: r.data.teleport?.target || { userid: row.userid, fname: row.fname, user_type: row.user_type },
      since: new Date().toISOString(),
    };
    localStorage.setItem(TELEPORT_KEY, JSON.stringify(meta));

    putSessionKeys({
      userid: String(row.userid),
      fname: row.fname,
      user_type: row.user_type || "default",
      username: row.username || meta.target.username,
      accessToken,
    });
    resetSupplierRetailerContext();
    resetPageAccessContext();
    await fetchUserAccess(row.userid);
    await fetchPageAccess(row.userid);
    signIn(row.user_type || "default");
    setTeleport({ from: meta.from, target: meta.target, since: meta.since, returnTo: meta.returnTo });
    navigate("/sales");
    return meta;
  };

  // Back to the master's own session, where they were.
  const teleportOut = async () => {
    const t = readTeleport();
    if (!t) {
      setTeleport(null);
      return false;
    }
    putSessionKeys(t.stash || {});
    localStorage.removeItem(TELEPORT_KEY);
    // Leave the target's page BEFORE the contexts are reset. The report pages
    // crash on an empty supplier list (Top10A/B `.map`) if they are still
    // mounted when it empties; navigating first lets the router unmount them
    // in the same render as the reset. The token is already the master's.
    navigate(t.returnTo || "/users");
    const masterId = t.stash?.userid;
    resetSupplierRetailerContext();
    resetPageAccessContext();
    if (masterId) {
      await fetchUserAccess(masterId);
      await fetchPageAccess(masterId);
    }
    signIn(t.stash?.user_type || "admin");
    setTeleport(null);
    return true;
  };

  const signOut = async () => {
    // Teleported: the sign-out reflex means "get me back to my own account".
    if (readTeleport()) {
      await teleportOut();
      return;
    }
    // Revoke the refresh token server-side so it can't be replayed if the
    // device is shared. Best-effort — never block sign-out on a network call.
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/login/sign-out`, { refreshToken });
      } catch (e) {
        // ignore — local clear is what matters
      }
    }
    setIsAuthenticated(false);
    setUserType(null);
    setRestoring(false);
    resetSupplierRetailerContext();
    resetPageAccessContext();
    try {
      sessionStorage.removeItem("spearbi_newdata_dismissed");
    } catch (e) {
      // ignore — sessionStorage may be unavailable
    }
    clearSessionKeys();
    navigate("/signin");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user_type, restoring, signIn, signOut, teleport, teleportIn, teleportOut }}>
      {children}
    </AuthContext.Provider>
  );
};
